/**
 * Settings TanStack Query hooks — profile + billing + usage.
 *
 * v1 reference: ``ando-frontend/app/pages/SettingsPage.tsx`` +
 * ``BillingPage.tsx``. The v1 pages mixed local component state with
 * direct API calls; v2 centralises in TanStack Query so the cache
 * survives navigation and forms can optimistically update.
 *
 * BE-side endpoints:
 *   - ``GET   /users/{user_id}``                — profile
 *   - ``PATCH /users/{user_id}/preferences``    — sparse merge
 *   - ``GET   /billing/subscription``           — current sub + limits
 *   - ``GET   /billing/usage``                  — monthly usage + cost
 *   - ``POST  /billing/portal``                 — Stripe portal URL
 *   - ``POST  /billing/checkout``               — Stripe Checkout URL
 *   - ``POST  /billing/cancel``                 — cancel at period end
 *   - ``GET   /billing/invoices``               — invoice list
 *
 * **BE gap** (May 2026):
 *   - No password-change endpoint (Zitadel-hosted UI).
 *   - No MFA enroll/verify/disable endpoints (Zitadel-hosted UI).
 *   - No org branding endpoints (deferred to a future iteration).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelBillingSubscription,
  createBillingCheckout,
  createBillingPortal,
  getBillingSubscription,
  getBillingUsage,
  getDailyBillingUsage,
  getUserProfile,
  getWeeklyBillingUsage,
  listBillingInvoices,
  updateUserPreferences,
  type CheckoutRequest,
  type UserPreferencesUpdate,
} from "@ando/core";

export const settingsQueryKeys = {
  root: ["settings"] as const,
  profile: (userId: string | null) =>
    ["settings", "profile", userId ?? null] as const,
  subscription: ["settings", "billing", "subscription"] as const,
  usage: ["settings", "billing", "usage"] as const,
  dailyUsage: ["settings", "billing", "usage", "daily"] as const,
  weeklyUsage: ["settings", "billing", "usage", "weekly"] as const,
  invoices: ["settings", "billing", "invoices"] as const,
};

export function useProfile(userId: string | null) {
  return useQuery({
    queryKey: settingsQueryKeys.profile(userId),
    queryFn: () => getUserProfile(userId as string),
    enabled: userId !== null,
    staleTime: 60_000,
  });
}

export function useUpdatePreferencesMutation(userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (update: UserPreferencesUpdate) =>
      updateUserPreferences(userId as string, update),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: settingsQueryKeys.profile(userId),
      });
    },
  });
}

export function useBillingSubscription() {
  return useQuery({
    queryKey: settingsQueryKeys.subscription,
    queryFn: getBillingSubscription,
    staleTime: 5 * 60_000,
  });
}

export function useBillingUsage() {
  return useQuery({
    queryKey: settingsQueryKeys.usage,
    queryFn: getBillingUsage,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * Daily cost-cap snapshot for the rolling-window guards V1's UsageMeter
 * surfaces alongside the monthly bar. Same staleTime as the monthly
 * usage so the three bars (daily / weekly / monthly) refetch in lockstep
 * and the UI never shows a "stale daily but fresh monthly" state.
 */
export function useBillingDailyUsage() {
  return useQuery({
    queryKey: settingsQueryKeys.dailyUsage,
    queryFn: getDailyBillingUsage,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useBillingWeeklyUsage() {
  return useQuery({
    queryKey: settingsQueryKeys.weeklyUsage,
    queryFn: getWeeklyBillingUsage,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useBillingInvoices() {
  return useQuery({
    queryKey: settingsQueryKeys.invoices,
    queryFn: listBillingInvoices,
    staleTime: 5 * 60_000,
  });
}

export function useOpenBillingPortalMutation() {
  return useMutation({
    mutationFn: () => createBillingPortal(),
    onSuccess: (res) => {
      // Redirect the user to the Stripe-hosted portal. Same pattern as v1.
      if (typeof window !== "undefined" && res.portal_url) {
        window.location.assign(res.portal_url);
      }
    },
  });
}

export function useStartCheckoutMutation() {
  return useMutation({
    mutationFn: (body: CheckoutRequest) => createBillingCheckout(body),
    onSuccess: (res) => {
      if (typeof window !== "undefined" && res.checkout_url) {
        window.location.assign(res.checkout_url);
      }
    },
  });
}

export function useCancelSubscriptionMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cancelBillingSubscription(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsQueryKeys.subscription });
    },
  });
}
